(() => {
  const elementName = 'ornata-fragrance-actions';
  const mobileInteractionQuery = '(max-width: 699px), (hover: none), (pointer: coarse)';
  const viewportPadding = 12;
  const openCardAttribute = 'data-ornata-fragrance-panel-open';
  const mobileLabelsAttribute = 'data-ornata-mobile-labels-visible';
  const mobileLabelsSessionKey = 'ornata-fragrance-mobile-labels-seen-v2';
  const mobileLabelsDuration = 2200;

  const usesMobileInteraction = () => window.matchMedia(mobileInteractionQuery).matches;
  const notePanel = (element) => element.querySelector?.('[data-fragrance-note-panel]');

  const clearPanelPosition = (panel) => {
    panel?.style.removeProperty('left');
    panel?.style.removeProperty('right');
  };

  const hideMobileLabels = (element) => {
    window.clearTimeout(element?.__ornataMobileLabelsTimer);
    if (!element) return;
    element.__ornataMobileLabelsTimer = 0;
    element.removeAttribute(mobileLabelsAttribute);
  };

  const mobileLabelsHaveBeenSeen = () => {
    try {
      return window.sessionStorage.getItem(mobileLabelsSessionKey) === 'true';
    } catch (_error) {
      return false;
    }
  };

  const markMobileLabelsSeen = () => {
    try {
      window.sessionStorage.setItem(mobileLabelsSessionKey, 'true');
    } catch (_error) {
      // Browsing can continue without session storage.
    }
  };

  const showMobileLabelsOnce = (element) => {
    if (!element || !usesMobileInteraction() || mobileLabelsHaveBeenSeen()) return;
    hideMobileLabels(element);
    element.setAttribute(mobileLabelsAttribute, 'true');
    markMobileLabelsSeen();
    element.__ornataMobileLabelsTimer = window.setTimeout(
      () => hideMobileLabels(element),
      mobileLabelsDuration
    );
  };

  const syncCardLayer = (element) => {
    const card = element.closest?.('product-card');
    if (!card) return;
    const panel = notePanel(element);
    const openOnMobile = usesMobileInteraction() && panel && !panel.hidden;
    card.toggleAttribute(openCardAttribute, Boolean(openOnMobile));
  };

  const positionMobileNotePanel = (element) => {
    const panel = notePanel(element);
    if (!panel) return;

    clearPanelPosition(panel);
    syncCardLayer(element);
    if (!usesMobileInteraction()) return;

    window.requestAnimationFrame(() => {
      if (!panel.isConnected || panel.hidden) return;

      const rightLimit = window.innerWidth - viewportPadding;
      const defaultRect = panel.getBoundingClientRect();
      if (defaultRect.left >= viewportPadding && defaultRect.right <= rightLimit) return;

      const defaultGap = Number.parseFloat(window.getComputedStyle(panel).right) || 36;
      if (defaultRect.left < viewportPadding) {
        panel.style.right = 'auto';
        panel.style.left = `${defaultGap}px`;
      }

      window.requestAnimationFrame(() => {
        if (!panel.isConnected || panel.hidden) return;

        const positionedRect = panel.getBoundingClientRect();
        let left = Number.parseFloat(panel.style.left);

        if (!Number.isFinite(left)) {
          left = positionedRect.left - element.getBoundingClientRect().left;
          panel.style.right = 'auto';
        }
        if (positionedRect.right > rightLimit) {
          left -= positionedRect.right - rightLimit;
        }
        if (positionedRect.left < viewportPadding) {
          left += viewportPadding - positionedRect.left;
        }

        panel.style.left = `${left}px`;
      });
    });
  };

  const installElement = (element) => {
    if (!element || element.__ornataMobileNotePositionInstalled) return;
    const panel = notePanel(element);
    if (!panel) return;

    element.__ornataMobileNotePositionInstalled = true;
    const observer = new MutationObserver(() => {
      if (panel.hidden) {
        clearPanelPosition(panel);
        syncCardLayer(element);
      } else {
        positionMobileNotePanel(element);
      }
    });
    observer.observe(panel, { attributes: true, attributeFilter: ['hidden'] });

    element.__ornataMobileNotePositionCleanup = () => {
      observer.disconnect();
      clearPanelPosition(panel);
      hideMobileLabels(element);
      element.closest?.('product-card')?.removeAttribute(openCardAttribute);
      element.__ornataMobileNotePositionCleanup = null;
      element.__ornataMobileNotePositionInstalled = false;
    };
  };

  const install = () => {
    const prototype = customElements.get(elementName)?.prototype;
    if (!prototype || prototype.__ornataMobileNotePanelFlipInstalled) return;

    const setExpanded = prototype.setExpanded;
    if (typeof setExpanded === 'function') {
      prototype.setExpanded = function (expanded, ...args) {
        const result = setExpanded.call(this, expanded, ...args);
        if (expanded && this.classList.contains('ornata-fragrance-actions--card')) {
          showMobileLabelsOnce(this);
        } else if (!expanded) {
          hideMobileLabels(this);
        }
        return result;
      };
    }

    const openNotePanel = prototype.openNotePanel;
    if (typeof openNotePanel === 'function') {
      prototype.openNotePanel = function (...args) {
        const result = openNotePanel.apply(this, args);
        installElement(this);
        positionMobileNotePanel(this);
        return result;
      };
    }

    const connectedCallback = prototype.connectedCallback;
    prototype.connectedCallback = function (...args) {
      const result = typeof connectedCallback === 'function'
        ? connectedCallback.apply(this, args)
        : undefined;
      installElement(this);
      if (this.classList.contains('ornata-fragrance-actions--gallery')) {
        showMobileLabelsOnce(this);
      }
      return result;
    };

    const disconnectedCallback = prototype.disconnectedCallback;
    prototype.disconnectedCallback = function (...args) {
      this.__ornataMobileNotePositionCleanup?.();
      return typeof disconnectedCallback === 'function'
        ? disconnectedCallback.apply(this, args)
        : undefined;
    };

    prototype.__ornataMobileNotePanelFlipInstalled = true;
    document.querySelectorAll(elementName).forEach((element) => {
      installElement(element);
      if (element.classList.contains('ornata-fragrance-actions--gallery')) {
        showMobileLabelsOnce(element);
      }
    });

    window.addEventListener('resize', () => {
      document.querySelectorAll(elementName).forEach((element) => {
        if (!usesMobileInteraction()) hideMobileLabels(element);
        const panel = notePanel(element);
        if (panel && !panel.hidden) positionMobileNotePanel(element);
        else syncCardLayer(element);
      });
    }, { passive: true });
  };

  if (customElements.get(elementName)) install();
  else customElements.whenDefined(elementName).then(install);
})();
